import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../components/app/Layout";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Landmark, ShieldCheck, ShieldAlert, History, Save, FileText, ArrowRight, RotateCcw, PenTool, Upload, Trash2 } from "lucide-react";

const FIELDS = [
    { key: "bank_name", label: "Bank Name", placeholder: "Banco Santander Argentina", col: 2 },
    { key: "account_holder", label: "Account Holder", placeholder: "CellarGlobal SA", col: 2 },
    { key: "account_number", label: "Account Number", placeholder: "0000-1111-2222-3333", col: 1 },
    { key: "swift", label: "SWIFT / BIC", placeholder: "BSCHESMM", col: 1 },
    { key: "iban", label: "IBAN", placeholder: "AR12 3456 7890 1234 5678", col: 2 },
    { key: "currency", label: "Currency", placeholder: "USD", col: 1 },
    { key: "bank_address", label: "Bank Address", placeholder: "Av. Corrientes 1234, Buenos Aires", col: 2 },
    { key: "contact_email", label: "Contact Email", placeholder: "finance@cellarglobal.com", col: 1 },
    { key: "contact_phone", label: "Contact Phone", placeholder: "+54 11 5555 5555", col: 1 },
];

const TABS = [
    { id: "config", label: "Configuration" },
    { id: "signature", label: "Signature" },
    { id: "preview", label: "PDF Preview" },
    { id: "audit", label: "Audit History" },
];

export default function AdminFinanceBank() {
    const { user, loading } = useAuth();
    const [tab, setTab] = useState("config");
    const [values, setValues] = useState({});
    const [initial, setInitial] = useState({});
    const [audit, setAudit] = useState([]);
    const [saving, setSaving] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [bankReady, setBankReady] = useState(false);
    const [missing, setMissing] = useState([]);
    const [restoringId, setRestoringId] = useState(null);
    const [signature, setSignature] = useState({ url: "", mime: "", uploaded_at: null, uploaded_by: null });
    const [uploadingSig, setUploadingSig] = useState(false);

    const reload = async () => {
        try {
            const [s, a] = await Promise.all([
                api.get("/bf/finance/bank-settings"),
                api.get("/bf/finance/bank-settings/audit", { params: { limit: 20 } }),
            ]);
            const clean = {};
            FIELDS.forEach(({ key }) => { clean[key] = s.data?.[key] ?? ""; });
            setValues(clean);
            setInitial({ ...clean, updated_at: s.data?.updated_at, updated_by: s.data?.updated_by });
            setAudit(a.data || []);
            setBankReady(!!s.data?.bank_ready);
            setMissing(s.data?.missing_fields || []);
            setSignature({
                url: s.data?.signature_data_url || "",
                mime: s.data?.signature_mime || "",
                uploaded_at: s.data?.signature_uploaded_at || null,
                uploaded_by: s.data?.signature_uploaded_by || null,
            });
            setLoaded(true);
        } catch (err) { toast.error(formatError(err)); }
    };

    useEffect(() => { if (user?.role === "admin") reload(); }, [user]);

    const dirtyKeys = useMemo(() => FIELDS
        .map(({ key }) => key)
        .filter((k) => (values[k] ?? "").trim() !== (initial[k] ?? "").trim()), [values, initial]);

    const requiredMissing = useMemo(() => {
        // "Solo validar que no estén vacíos" applies to core banking fields.
        const core = ["bank_name", "account_holder", "account_number", "iban", "currency"];
        return core.filter((k) => !(values[k] || "").trim());
    }, [values]);

    const save = async () => {
        if (requiredMissing.length > 0) {
            toast.error(`Complete required fields: ${requiredMissing.join(", ")}`);
            return;
        }
        setSaving(true);
        try {
            const payload = {};
            FIELDS.forEach(({ key }) => { payload[key] = (values[key] || "").trim(); });
            await api.put("/bf/finance/bank-settings", payload);
            toast.success(dirtyKeys.length ? `Saved. ${dirtyKeys.length} field(s) updated.` : "Saved.");
            await reload();
        } catch (err) { toast.error(formatError(err)); }
        finally { setSaving(false); }
    };

    const restore = async (entry) => {
        const ts = fmtTs(entry.created_at);
        if (!window.confirm(`Restore bank configuration to the version from ${ts} by ${entry.actor}?\n\nCurrent values will be overwritten (a new audit entry will record the rollback).`)) return;
        setRestoringId(entry.id);
        try {
            await api.post(`/bf/finance/bank-settings/restore/${entry.id}`);
            toast.success("Bank configuration restored.");
            await reload();
        } catch (err) { toast.error(formatError(err)); }
        finally { setRestoringId(null); }
    };

    const uploadSignature = async (file) => {
        if (!file) return;
        if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
            toast.error("Signature must be PNG, JPEG or WEBP.");
            return;
        }
        if (file.size > 400 * 1024) {
            toast.error("Signature must be under 400 KB.");
            return;
        }
        setUploadingSig(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post("/bf/finance/bank-settings/signature", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Signature uploaded.");
            await reload();
        } catch (err) { toast.error(formatError(err)); }
        finally { setUploadingSig(false); }
    };

    const removeSignature = async () => {
        if (!window.confirm("Remove the signature? Future invoices will no longer include it.")) return;
        try {
            await api.delete("/bf/finance/bank-settings/signature");
            toast.success("Signature removed.");
            await reload();
        } catch (err) { toast.error(formatError(err)); }
    };

    if (loading) return <Layout><div className="px-6 py-32 text-[#71717A]">Loading...</div></Layout>;
    if (!user) return <Navigate to="/login?redirect=/admin/finance/bank" replace />;
    if (user.role !== "admin") return <Navigate to="/" replace />;

    return (
        <Layout>
            <div className="px-6 md:px-12 lg:px-16 pt-10 pb-32">
                <div className="flex items-center gap-3">
                    <Landmark className="w-5 h-5 text-[#C9A961]" />
                    <p className="overline">Business Finance · Bank Configuration</p>
                </div>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mt-3">
                    <div>
                        <h1 className="font-display text-5xl md:text-6xl">Bank Configuration</h1>
                        <p className="text-sm text-[#A1A1AA] mt-3 max-w-2xl">
                            Datos bancarios inyectados automáticamente en cada factura PDF. Todo cambio queda
                            registrado en el log de auditoría con diff campo por campo.
                        </p>
                    </div>
                    <div className="text-xs text-[#71717A] leading-relaxed">
                        <BankReadyPill ready={bankReady} missing={missing} />
                        <div className="mt-2 flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-[#C9A961]" /> Admin only · Immutable audit</div>
                        {initial.updated_at && (
                            <div className="mt-1">Last update: <span className="text-[#E4E4E7]">{fmtTs(initial.updated_at)}</span> by <span className="text-[#E4E4E7]">{initial.updated_by || "—"}</span></div>
                        )}
                    </div>
                </div>

                <div className="flex gap-2 mt-10 border-b border-[#27272A]" data-testid="bank-tabs">
                    {TABS.map((t) => (
                        <button key={t.id} onClick={() => setTab(t.id)} data-testid={`bank-tab-${t.id}`}
                            className={`px-6 py-4 text-sm tracking-[0.15em] uppercase relative ${tab === t.id ? "text-[#C9A961]" : "text-[#71717A] hover:text-white"}`}>
                            {t.label}
                            {tab === t.id && <span className="absolute bottom-0 left-0 right-0 h-px bg-[#C9A961]" />}
                        </button>
                    ))}
                </div>

                {tab === "config" && (
                    <div className="mt-10 grid gap-8 max-w-5xl" data-testid="bank-config-form">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            {FIELDS.map(({ key, label, placeholder, col }) => (
                                <div key={key} className={col === 2 ? "md:col-span-2" : ""}>
                                    <label className="overline block mb-2" htmlFor={`bank-${key}`}>{label}</label>
                                    <input
                                        id={`bank-${key}`}
                                        data-testid={`bank-input-${key}`}
                                        className="input-line"
                                        value={values[key] || ""}
                                        placeholder={placeholder}
                                        onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                                    />
                                    {(values[key] || "").trim() !== (initial[key] || "").trim() && loaded && (
                                        <p className="text-[10px] uppercase tracking-widest text-[#C9A961] mt-1">Modified</p>
                                    )}
                                </div>
                            ))}
                        </div>

                        {requiredMissing.length > 0 && (
                            <div className="text-xs text-[#F97316] border border-[#27272A] p-4">
                                Required (cannot be empty): {requiredMissing.join(", ")}
                            </div>
                        )}

                        <div className="flex items-center gap-4 pt-2">
                            <button onClick={save} disabled={saving || dirtyKeys.length === 0} className="btn-gold" data-testid="bank-save-btn">
                                <Save className="w-4 h-4" /> {saving ? "Saving..." : `Save${dirtyKeys.length ? ` (${dirtyKeys.length})` : ""}`}
                            </button>
                            {dirtyKeys.length > 0 && (
                                <button onClick={() => setValues(Object.fromEntries(FIELDS.map(({ key }) => [key, initial[key] || ""])))} className="btn-outline" data-testid="bank-reset-btn">
                                    Discard changes
                                </button>
                            )}
                            <span className="text-xs text-[#71717A]">
                                {dirtyKeys.length === 0 ? "No pending changes" : `${dirtyKeys.length} field(s) pending`}
                            </span>
                        </div>
                    </div>
                )}

                {tab === "signature" && (
                    <div className="mt-10 max-w-3xl" data-testid="bank-signature">
                        <div className="flex items-center gap-2 text-xs text-[#71717A] mb-6">
                            <PenTool className="w-3.5 h-3.5" /> Firma autorizada inyectada bajo los datos bancarios en cada PDF de factura
                        </div>
                        <div className="border border-[#27272A] bg-[#0A0A0A] p-8">
                            {signature.url ? (
                                <>
                                    <p className="overline mb-4">Current Signature</p>
                                    <div className="border border-[#27272A] bg-white p-6 inline-block">
                                        {/* Light background so signatures with dark ink stay legible. */}
                                        <img src={signature.url} alt="Authorized signature"
                                            className="max-h-32 max-w-[24rem] object-contain block"
                                            data-testid="bank-signature-preview" />
                                    </div>
                                    <div className="text-xs text-[#71717A] mt-4">
                                        Uploaded: <span className="text-[#E4E4E7]">{fmtTs(signature.uploaded_at)}</span>
                                        {signature.uploaded_by && <> · by <span className="text-[#E4E4E7]">{signature.uploaded_by}</span></>}
                                        <> · <span className="uppercase tracking-widest">{signature.mime?.split("/")?.[1] || ""}</span></>
                                    </div>
                                    <div className="flex gap-3 mt-6">
                                        <label className="btn-outline cursor-pointer" data-testid="bank-signature-replace">
                                            <Upload className="w-4 h-4" /> Replace
                                            <input type="file" className="hidden" accept="image/png,image/jpeg,image/webp"
                                                onChange={(e) => uploadSignature(e.target.files?.[0])} disabled={uploadingSig} />
                                        </label>
                                        <button onClick={removeSignature} className="btn-outline" data-testid="bank-signature-remove">
                                            <Trash2 className="w-4 h-4" /> Remove
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <p className="overline mb-2">No signature uploaded</p>
                                    <p className="text-sm text-[#A1A1AA] mt-2 max-w-md">
                                        Sube una imagen PNG, JPEG o WEBP con fondo transparente (max 400 KB). Se mostrará
                                        debajo de los datos bancarios en cada factura PDF generada.
                                    </p>
                                    <label className="btn-gold inline-flex cursor-pointer mt-6" data-testid="bank-signature-upload">
                                        <Upload className="w-4 h-4" /> {uploadingSig ? "Uploading..." : "Upload Signature"}
                                        <input type="file" className="hidden" accept="image/png,image/jpeg,image/webp"
                                            onChange={(e) => uploadSignature(e.target.files?.[0])} disabled={uploadingSig} />
                                    </label>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {tab === "preview" && (
                    <div className="mt-10 max-w-3xl" data-testid="bank-preview">
                        <div className="flex items-center gap-2 text-xs text-[#71717A] mb-4">
                            <FileText className="w-3.5 h-3.5" /> Vista previa de los datos bancarios en el PDF de factura
                        </div>
                        <div className="border border-[#27272A] bg-[#0A0A0A] p-10">
                            <p className="overline mb-6">Bank Details</p>
                            <div className="divide-y divide-[#27272A]">
                                {FIELDS.map(({ key, label }) => (
                                    <div key={key} className="grid grid-cols-3 gap-4 py-3 text-sm">
                                        <div className="text-[#C9A961] uppercase tracking-widest text-xs col-span-1">{label}</div>
                                        <div className="text-[#E4E4E7] col-span-2 break-all">{(values[key] || "").trim() || <span className="text-[#71717A]">—</span>}</div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-[10px] text-[#71717A] mt-6 uppercase tracking-widest">
                                Auto-injected into every generated invoice PDF
                            </p>
                        </div>
                    </div>
                )}

                {tab === "audit" && (
                    <div className="mt-10 max-w-5xl" data-testid="bank-audit">
                        <div className="flex items-center gap-2 text-xs text-[#71717A] mb-6">
                            <History className="w-3.5 h-3.5" /> Últimas 20 modificaciones · Registro inmutable
                        </div>
                        {audit.length === 0 ? (
                            <div className="border border-[#27272A] p-16 text-center text-[#A1A1AA]">
                                No hay modificaciones registradas.
                            </div>
                        ) : (
                            <ul className="space-y-4">
                                {audit.map((entry) => (
                                    <AuditRow key={entry.id} entry={entry} onRestore={restore} restoring={restoringId === entry.id} />
                                ))}
                            </ul>
                        )}
                    </div>
                )}
            </div>
        </Layout>
    );
}

function AuditRow({ entry, onRestore, restoring }) {
    const diff = entry?.detail?.diff || {};
    const keys = Object.keys(diff);
    const canRestore = !!entry?.detail?.snapshot_after;
    const isRestoreEntry = entry?.action === "bank_settings_restore";
    return (
        <li className="border border-[#27272A] bg-[#0A0A0A]" data-testid={`bank-audit-${entry.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-[#27272A]">
                <div className="text-xs text-[#71717A]">
                    <span className="text-[#E4E4E7]">{fmtTs(entry.created_at)}</span>
                    <span className="mx-3">·</span>
                    <span className="text-[#C9A961]">{entry.actor}</span>
                    {isRestoreEntry && <span className="ml-3 text-[10px] uppercase tracking-widest text-[#C9A961] border border-[#C9A961] px-2 py-0.5">Rollback</span>}
                </div>
                <div className="flex items-center gap-4">
                    <span className="text-[10px] uppercase tracking-widest text-[#71717A]">
                        {keys.length === 0 ? "No changes" : `${keys.length} field(s) changed`}
                    </span>
                    {canRestore ? (
                        <button
                            onClick={() => onRestore(entry)}
                            disabled={restoring}
                            className="btn-outline !py-1.5 !px-3 !text-[10px] tracking-widest"
                            data-testid={`bank-restore-${entry.id}`}
                            title="Restore bank configuration to this version"
                        >
                            <RotateCcw className="w-3 h-3" /> {restoring ? "Restoring..." : "Restore"}
                        </button>
                    ) : (
                        <span className="text-[10px] uppercase tracking-widest text-[#3F3F46]" title="Legacy entry: no snapshot stored">
                            Restore unavailable
                        </span>
                    )}
                </div>
            </div>
            {keys.length > 0 && (
                <div className="divide-y divide-[#27272A]">
                    {keys.map((k) => (
                        <div key={k} className="grid grid-cols-12 gap-3 items-center px-5 py-3 text-sm">
                            <div className="col-span-3 text-xs text-[#C9A961] uppercase tracking-widest">{k.replace(/_/g, " ")}</div>
                            <div className="col-span-4 text-[#71717A] line-through break-all">{diff[k]?.old || "—"}</div>
                            <div className="col-span-1 text-[#C9A961] flex justify-center"><ArrowRight className="w-3.5 h-3.5" /></div>
                            <div className="col-span-4 text-[#E4E4E7] break-all">{diff[k]?.new || "—"}</div>
                        </div>
                    ))}
                </div>
            )}
        </li>
    );
}

function BankReadyPill({ ready, missing }) {
    if (ready) {
        return (
            <span className="inline-flex items-center gap-2 border border-[#C9A961] text-[#C9A961] px-3 py-1 text-[10px] uppercase tracking-widest" data-testid="bank-ready-pill">
                <ShieldCheck className="w-3 h-3" /> Bank Ready
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-2 border border-[#F97316] text-[#F97316] px-3 py-1 text-[10px] uppercase tracking-widest" data-testid="bank-notready-pill" title={missing?.length ? `Missing: ${missing.join(", ")}` : "Bank incomplete"}>
            <ShieldAlert className="w-3 h-3" /> Bank Incomplete{missing?.length ? ` · ${missing.length}` : ""}
        </span>
    );
}

function fmtTs(iso) {
    if (!iso) return "—";
    return String(iso).replace("T", " ").slice(0, 19) + " UTC";
}
